import { Injectable } from '@angular/core';
import {
  HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES,
} from '@ptl/xhr';

import { PRODUCT_GROUPS, PRODUCT_GROUP_NAMES, PRODUCT_TYPES } from 'src/app/angular-elements/plan-selection/enums/product-groups.enum';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PlansStateStructure } from '../configs/interfaces/plansStateStructure.interface';
import { Product } from '../interfaces/product.interface';
import { MedicareAdvantage_and_PDP_PlanAttributes, PlanAttributes, ProviderNetworkMap, SummaryView } from '../interfaces/plan-attributes.interface';
import { PlanDetailsStateStructure } from '../configs/interfaces/planDetailsStateStructure.interface';
import { ProductTabCollection } from '../interfaces/product-tab.interface';
import { PlanDetailsIndividualMarketRequestPayload, PlanDetailsMedicareSupplementRequestPayload, PlanDetailsRequestPayload } from '../services/interfaces/plan-details/plan-details.request.interface';
import { PlanServiceResponse } from '../interfaces/plan-service-response.interface';
import { DOCTOR_VENDOR_TYPE } from '../../medical-information/doctor-information/configs/enums/doctor-vendor-type.enum';
import { ZipCountyStateStructure } from '../../shared/interfaces/zipCountyStateStructure.interface';
import { STATE_CODE_LABEL_MAPPING } from 'src/app/shared/enums/state-code-label.map';
import { DoctorInformationService as DoctorInformationKyruusService } from '../../medical-information/doctor-information-kyruus/services/doctor-information.service';
import { DoctorStateKyruus, DOCTOR_STATE_REDUX_ID_KYRUUS } from '../../medical-information/doctor-information-kyruus/configs/reducers/doctors.reducer';
import { DoctorStateSapphireDigital, DOCTOR_STATE_REDUX_ID_SAPPHIRE_DIGITAL } from '../../medical-information/doctor-information-sapphire-digital/configs/reducers/doctors.reducer';
import { DoctorVendorTypeState, DOCTOR_VENDOR_TYPE_STATE_REDUX_ID } from '../../medical-information/doctor-information/configs/reducers/doctor-information.reducer';
import { Doctor as DoctorSapphireDigital } from '../../medical-information/doctor-information-sapphire-digital/configs/interfaces/doctors.interface';

@Injectable({
  providedIn: 'root'
})

export class PlanUtilityService {

  constructor(
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private doctorServiceKyruus: DoctorInformationKyruusService
  ) { }

  /** PRODUCT AGNOSTIC */
  getSelectedPlan(productType?: string): Product<PlanAttributes, SummaryView> {
    let selectedPlan: Product<PlanAttributes, SummaryView>;
    try {
      const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
      const plansFromState = this.getPlansByProductGroupAsArray();

      selectedPlan = plansFromState.filter(plan => plan.attributes.PLAN_ID === planState.planIdForEnrollment)[0];
    } catch {
      selectedPlan = null;
    }

    // WHB is not a true plan and will not be found in the above filtering
    if (!selectedPlan && productType === PRODUCT_TYPES.WHOLE_HEALTH_BALANCE) {
        selectedPlan = {
            productName: PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE,
            attributes: {
                PRODUCT_GROUP: PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE
            } as PlanAttributes
        } as Product<PlanAttributes, SummaryView>;
    }

    return selectedPlan;
  }

  getAllPlansAsMap(): Map<PRODUCT_GROUP_NAMES, Product<PlanAttributes, SummaryView>[]> {
    
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    if (!planState) { return new Map(); }

    const products = new Map();

    for (const product in planState.plansByPlanType) {
      const plansByProduct = planState.plansByPlanType[product];
      const plans = [];
      for (const plan in plansByProduct) {
        plans.push(plansByProduct[plan]);
      }
      products.set(product, plans);
    }

    return products;
  }

  getPlansForPlanDetails(payload: PlanDetailsRequestPayload
    | PlanDetailsMedicareSupplementRequestPayload
    | PlanDetailsIndividualMarketRequestPayload) {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/retrievePlans/plans`,
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.GOOGLE_CLOUD);
    return this.httpClient.makeRequest<PlanServiceResponse>(requestConfig);


  }


  async getPlansForPlanDetailsIndividual(
    payload:
      | PlanDetailsRequestPayload
      | PlanDetailsMedicareSupplementRequestPayload
      | PlanDetailsIndividualMarketRequestPayload
  ): Promise<PlanServiceResponse> {

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/retrieveIndividualPlans/individualplans`,
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.GOOGLE_CLOUD);
    return this.httpClient.makeRequest<PlanServiceResponse>(requestConfig);
  }

  getPlansByProductGroupAsArray(productGroup?: string): Product<PlanAttributes, SummaryView>[] {
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    if (!planState || !planState.plansByPlanType) { return []; }

    const plansOfType = planState.plansByPlanType[productGroup || planState.activeProductGroup];
    if (!plansOfType) { return []; }

    /** Set Active Plans to display */
    // Convert from collection to array
    const productIds = Object.keys(plansOfType);
    const plans = [];
    for (const id of productIds) {
      plans.push(plansOfType[id]);
    }

    return plans;
  }

  getPlanDetailsByProductGroupAsArray(): Product<PlanAttributes, SummaryView>[] {
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const planDetailsState: PlanDetailsStateStructure = this.reducerUtilityService.getState('PlanDetails');
    if (!planDetailsState) { return []; }

    const plansOfType = planDetailsState.planDetails[planState.activeProductGroup];
    if (!plansOfType) { return []; }

    /** Set Active Plans to display */
    // Convert from collection to array
    const productIds = Object.keys(plansOfType);
    const plans = [];
    for (const id of productIds) {
      plans.push(plansOfType[id]);
    }

    return plans;
  }

  getPlanMinimumAndMaximumPrices(plans?: Product<PlanAttributes, SummaryView>[]): { min: number, max: number } {
    let prices;

    if (plans) {
      prices = plans.map(product => product.price);
    } else {
      prices = this.getPlansByProductGroupAsArray().map(product => product.price);
    }

    if (prices.length > 0) {
      return {
        min: Math.floor(Math.min(...prices)),
        max: Math.ceil(Math.max(...prices))
      };
    } else {
      return { min: 0, max: 0 };
    }
  }


  // TODO: Try and make this only cound plans available? Is this even necessary.
  getPlanTabLabelAndNumberAvailable(): ProductTabCollection {
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const planTypesAvailable = planState.productGroupsForCounty;
    const plansByPlanType = planState.plansByPlanType;

    if (!planTypesAvailable || !plansByPlanType) {
      return null;
    }

    let planTabLabelAndNumber: ProductTabCollection = null;

    Object.keys(planTypesAvailable).forEach(key => {

      planTabLabelAndNumber = planTabLabelAndNumber || {};

      const plansByKey = planState.plansByPlanType[key];
      const numPlans = plansByKey ? Object.keys(plansByKey).length : 0;

      planTabLabelAndNumber[key] = {
        type: key as PRODUCT_GROUPS,
        hasProducts: true,
        number: numPlans
      };

    });

    /** Add a whole health balance object if Medigap plans will be available */
    if ((planTabLabelAndNumber[PRODUCT_GROUPS.MEDIGAP]
      || planTabLabelAndNumber[PRODUCT_GROUPS.MEDICARE_SUPPLEMENT])) {
      planTabLabelAndNumber[PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE] = {
        type: PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE,
        hasProducts: false,
        number: 0
      };
    }

    return planTabLabelAndNumber;
  }

  // Retrieve a mapping of providers and if the are in or out of network
  getProvidersCoveredByPlan(plan: Product<PlanAttributes, SummaryView>): ProviderNetworkMap {
    const providerInfoMap: ProviderNetworkMap = {
      count: 0,
      providers: {
        inNetwork: [],
        outOfNetwork: []
      }
    };

    const productGroup = plan.attributes?.PRODUCT_GROUP?.toUpperCase();

    const isMedAdvantageProduct = productGroup ? productGroup === PRODUCT_GROUPS.MEDICARE_ADVANTAGE.toUpperCase() : false;
    const isOffXProduct = productGroup ? productGroup === PRODUCT_GROUPS.OFF_EXCHANGE.toUpperCase() : false;
    
    // TODO: make this configurable
    /** 10-28-2026 - Alex said configurability is for what product groups would use which vendor
     * something to consider for future-proofing if we add other product groups that need to use these vendors
    */
    if (isMedAdvantageProduct || isOffXProduct) {

      const doctorVendorState: DoctorVendorTypeState = this.reducerUtilityService.getState(DOCTOR_VENDOR_TYPE_STATE_REDUX_ID);

      if (doctorVendorState.vendorType === DOCTOR_VENDOR_TYPE.KYRUUS) {
        
        const doctorState: DoctorStateKyruus = this.reducerUtilityService.getState(DOCTOR_STATE_REDUX_ID_KYRUUS);

        doctorState.doctors.forEach(doc => {
          const medAdvAttr = (plan.attributes as MedicareAdvantage_and_PDP_PlanAttributes);
          if (!medAdvAttr.PRODUCT_CODE) {
            console.error("missing product code");
            return;
          }

          const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
          const state = STATE_CODE_LABEL_MAPPING[zipCountyState.county && zipCountyState.county.state];
          
          const plansAcceptedAttribute = this.doctorServiceKyruus.getPlansAcceptedAttribute(doc.kyruusProvider);

          const filteredCategories = plansAcceptedAttribute.value.filter( category => category.name === state);

          let isPlanAccepted = false;

          if (filteredCategories[0]?.plans?.length > 0) {
            isPlanAccepted = filteredCategories[0].plans.filter(plan => plan.productCode === medAdvAttr.PRODUCT_CODE).length > 0;
          } else if (filteredCategories[0]?.plans?.length === 0 && filteredCategories[0]?.subCategories?.length > 0) {
              let tempPlansArray = [];
              for (const category of filteredCategories[0].subCategories) {
                tempPlansArray = tempPlansArray.concat(category.plans.filter(plan => plan.productCode === medAdvAttr.PRODUCT_CODE));
              }
              isPlanAccepted = tempPlansArray.length > 0;
          }
      
          if (isPlanAccepted) {
            providerInfoMap.providers.inNetwork.push(doc);
            providerInfoMap.count++;
          } else {
            providerInfoMap.providers.outOfNetwork.push(doc);
          }
        });
      }
      else if (doctorVendorState.vendorType === DOCTOR_VENDOR_TYPE.SAPPHIRE_DIGITAL) {
        const doctorState: DoctorStateSapphireDigital = this.reducerUtilityService.getState(DOCTOR_STATE_REDUX_ID_SAPPHIRE_DIGITAL);

        doctorState.doctors.forEach(doc => {
          const medAdvAttr = (plan.attributes as MedicareAdvantage_and_PDP_PlanAttributes);

          const acceptedNumber: number = doc.networksAccepted.filter(network => {
            if (network.id && medAdvAttr.PROVIDER_NETWORK_ID) {
              return network.id.toString() === medAdvAttr.PROVIDER_NETWORK_ID;
            }
            else if (network.id && medAdvAttr.PROVIDER_PLAN_ID) {
              return network.id.toString() === medAdvAttr.PROVIDER_PLAN_ID;
            }
          }).length;

          if (acceptedNumber > 0) {
            providerInfoMap.providers.inNetwork.push(doc as DoctorSapphireDigital);
            providerInfoMap.count++;
          }
          else {
            providerInfoMap.providers.outOfNetwork.push(doc as DoctorSapphireDigital);
          }
        });
      }
    }

    return providerInfoMap;
  }
}
